import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSiteContent } from "@/lib/siteContent";

/**
 * Referenzaktion — zentraler Hook. Sichtbarkeits-Regel:
 *  - `aktion.enabled === true` im CMS (Master-Switch aus dem Admin)
 *  - Auf `/admin/*`-Routen bewusst unterdrückt (Admin-UI clean halten)
 *
 * Kein UTM-Check mehr: Die Aktion ist für alle öffentlichen Besucher
 * sichtbar, sobald der Admin sie einschaltet. Die alten CMS-Felder
 * `trigger_utm_param` / `trigger_utm_value` bleiben aus Rückwärts-
 * Kompatibilität im Objekt, werden aber nicht mehr ausgewertet.
 */
export type AktionContent = {
    enabled: boolean;
    trigger_utm_param: string;
    trigger_utm_value: string;
    banner_text: string;
    banner_cta_label: string;
    hero_chip_text: string;
    section_eyebrow: string;
    section_headline: string;
    section_body: string;
    price_regular_text: string;
    price_advantage_text: string;
    section_cta_label: string;
    slots_visible: boolean;
    slots_remaining: number;
    slots_total: number;
    slots_label_template: string;
    slots_shimmer_intensity: "off" | "subtle" | "medium" | "strong";
};

const DEFAULTS: AktionContent = {
    enabled: false,
    trigger_utm_param: "utm_campaign",
    trigger_utm_value: "referenzaktion",
    banner_text: "Exklusive Referenzaktion · 20 Immobilien · 80 % Preisvorteil für Referenzkunden",
    banner_cta_label: "Mehr erfahren →",
    hero_chip_text: "Exklusive Referenzaktion · 80 % Preisvorteil",
    section_eyebrow: "Exklusive Referenzaktion",
    section_headline: "20 Immobilien. 80 % Preisvorteil.",
    section_body: "",
    price_regular_text: "Regulärer Preis: ab 199 €",
    price_advantage_text: "80 % Preisvorteil – nur für ausgewählte Referenzkunden",
    section_cta_label: "Für Referenzaktion bewerben →",
    slots_visible: true,
    slots_remaining: 20,
    slots_total: 20,
    slots_label_template: "{remaining} von {total} noch frei",
    slots_shimmer_intensity: "medium",
};

export const AKTION_SESSION_KEY = "hf_aktion_active";
export const AKTION_BANNER_DISMISSED_KEY = "hf_aktion_banner_dismissed";

export function isAktionActiveNow(cms: AktionContent): boolean {
    return cms.enabled === true;
}

export function useAktion(): AktionContent & { active: boolean } {
    const site = useSiteContent();
    const cms = site.getObject<Partial<AktionContent>>("aktion", {});
    const merged: AktionContent = useMemo(() => ({
        ...DEFAULTS,
        ...cms,
        // Force boolean coercion — CMS könnte string liefern.
        enabled: cms?.enabled !== undefined ? cms.enabled === true : DEFAULTS.enabled,
        slots_visible: cms?.slots_visible !== undefined
            ? cms.slots_visible !== false
            : DEFAULTS.slots_visible,
        slots_remaining: Number.isFinite(Number(cms?.slots_remaining))
            ? Number(cms?.slots_remaining)
            : DEFAULTS.slots_remaining,
        slots_total: Number.isFinite(Number(cms?.slots_total))
            ? Number(cms?.slots_total)
            : DEFAULTS.slots_total,
        slots_shimmer_intensity: (["off", "subtle", "medium", "strong"] as const).includes(
            cms?.slots_shimmer_intensity as AktionContent["slots_shimmer_intensity"],
        )
            ? (cms?.slots_shimmer_intensity as AktionContent["slots_shimmer_intensity"])
            : DEFAULTS.slots_shimmer_intensity,
    }), [cms]);

    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    const [active, setActive] = useState<boolean>(() => !isAdmin && merged.enabled);

    useEffect(() => {
        // Admin-Seiten schließen die Aktion explizit aus — dort wollen wir
        // weder Banner noch Chip noch Section rendern.
        if (isAdmin) {
            setActive(false);
            return;
        }
        const now = merged.enabled === true;
        if (now) {
            // Session-Marker weiterhin setzen (Analytics-Attribution)
            try { sessionStorage.setItem(AKTION_SESSION_KEY, "1"); } catch { /* ignore */ }
        }
        setActive(now);
    }, [merged.enabled, isAdmin]);

    return { ...merged, active };
}
